import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { CHARACTERS } from '../lib/levels';
import { useT } from '../lib/i18n';

export default function Quiz() {
  const { t } = useT();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [batchDone, setBatchDone] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/quiz/daily');
        setQuestions(data.questions || []);
      } catch { toast.error('Could not load quiz'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center font-heading text-navy text-xl">Loading…</div>;

  const q = questions[idx];

  const submit = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/quiz/submit', { question_id: q.id, answer: selected });
      setResult(data);
      setSessionCount((c) => c + 1);
      if (data.is_correct) toast.success(`+${data.points_earned} pts 🎉`);
      else toast.info(`+${data.points_earned} pt`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Submit failed';
      // Demo hardening: never surface quiz-limit / upgrade messaging from any stale server.
      if (/limit|per day|upgrade|premium|free.tier|quota/i.test(String(msg))) {
        return;
      }
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const next = () => {
    setResult(null); setSelected(null);
    if (idx + 1 < questions.length) setIdx(idx + 1);
    else setBatchDone(true);
  };

  const keepGoing = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get('/quiz/more', { params: { count: 3 } });
      const more = data.questions || [];
      if (!more.length) { toast.info('No more questions'); return; }
      setQuestions((prev) => [...prev, ...more]);
      setIdx((i) => i + 1);
      setBatchDone(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Load failed'); }
    finally { setLoadingMore(false); }
  };

  const finish = async () => { await refresh(); navigate('/dashboard'); };

  if (batchDone) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-4">
        <div className="bg-white rounded-3xl p-8 shadow-squishy border-2 border-cream max-w-md w-full text-center animate-bounce-in" data-testid="keep-going-card">
          <div className="w-16 h-16 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-teal" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-navy mb-2">{t('quiz.great_job')}</h1>
          <p className="text-navy/70 mb-1">{t('quiz.session_answered', { n: sessionCount })}</p>
          <p className="text-navy/60 text-sm mb-6">{t('quiz.session_hint')}</p>
          <div className="space-y-3">
            <button onClick={keepGoing} disabled={loadingMore}
              className="w-full h-14 rounded-full bg-gold text-navy font-heading font-bold text-lg shadow-gold hover:scale-[1.02] active:scale-95 transition-transform duration-200 disabled:opacity-60"
              data-testid="keep-going-button">
              {loadingMore ? t('quiz.loading_more') : t('quiz.keep_going')}
            </button>
            <button onClick={finish}
              className="w-full h-12 rounded-full bg-white border-2 border-navy text-navy font-heading font-bold hover:scale-[1.02] active:scale-95 transition-transform duration-200"
              data-testid="finish-session-button">{t('quiz.finished_btn')}</button>
          </div>
        </div>
      </div>
    );
  }

  if (!q) return <div className="min-h-screen flex items-center justify-center font-heading text-navy text-xl">{t('quiz.no_questions')}</div>;

  const coachChar = result?.coach?.character_key && CHARACTERS[result.coach.character_key];

  return (
    <div className="min-h-screen bg-cream">
      <header className="px-4 py-4 flex items-center justify-between max-w-2xl mx-auto">
        <button onClick={finish} className="w-11 h-11 rounded-full bg-white shadow-squishy flex items-center justify-center hover:scale-105 transition-transform duration-200" data-testid="quiz-back-button" aria-label="Back">
          <ArrowLeft className="w-5 h-5 text-navy" />
        </button>
        <div className="text-navy/70 font-heading font-semibold" data-testid="quiz-progress">{t('quiz.answered')}: {sessionCount}</div>
        <div className="w-11" />
      </header>

      <main className="max-w-2xl mx-auto px-4 pb-10" data-testid="quiz-screen">
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-squishy border-2 border-cream animate-bounce-in" data-testid="quiz-question-card">
          <div className="inline-block px-3 py-1 rounded-full bg-teal/10 text-teal font-heading font-bold text-xs uppercase tracking-wider mb-4" data-testid="quiz-category">
            {t(`subject.${q.category}`) || q.category}
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy leading-tight mb-6" data-testid="quiz-question-text">{q.question}</h1>

          <div className="space-y-3">
            {q.options.map((opt) => {
              const isSelected = selected === opt;
              const isCorrect = result && opt === result.correct_answer;
              const isWrong = result && isSelected && opt !== result.correct_answer;
              let cls = 'border-slate-200 hover:border-teal/60 hover:bg-teal/5';
              if (isSelected && !result) cls = 'border-teal bg-teal/10';
              if (isCorrect) cls = 'border-teal bg-teal/15';
              if (isWrong) cls = 'border-red-400 bg-red-50';
              return (
                <button key={opt} onClick={() => !result && setSelected(opt)} disabled={!!result}
                  className={`w-full flex items-center justify-between gap-3 p-4 rounded-2xl border-2 text-left transition-colors duration-200 ${cls}`}
                  data-testid={`quiz-option-${opt.replace(/\s+/g, '-').toLowerCase()}`}>
                  <span className="font-semibold text-navy text-base md:text-lg">{opt}</span>
                  {isCorrect && <CheckCircle2 className="w-5 h-5 text-teal shrink-0" />}
                  {isWrong && <XCircle className="w-5 h-5 text-red-500 shrink-0" />}
                </button>
              );
            })}
          </div>

          {/* Coach card */}
          {result && result.coach && (
            <div className={`mt-6 rounded-2xl border-2 p-4 flex gap-3 items-start ${result.is_correct ? 'border-teal bg-teal/10' : 'border-gold bg-gold/10'}`} data-testid="coach-message-card">
              {coachChar && (
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-squishy shrink-0">
                  <img src={coachChar.image} alt={result.coach.character_name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="min-w-0">
                <div className="font-heading font-bold text-navy text-sm" data-testid="coach-character-name">{result.coach.character_name}</div>
                <div className="text-navy leading-snug mt-0.5" data-testid="coach-message-text">{result.coach.message}</div>
                <div className="text-xs text-navy/60 mt-2">+{result.points_earned} {t('quiz.pts_earned')} · {t('quiz.total_pts')} {result.total_points}</div>
              </div>
            </div>
          )}

          <div className="mt-6">
            {!result ? (
              <button onClick={submit} disabled={!selected || submitting}
                className="w-full h-14 rounded-full bg-gold text-navy font-heading font-bold text-lg shadow-gold hover:scale-[1.02] active:scale-95 transition-transform duration-200 disabled:opacity-40 disabled:pointer-events-none"
                data-testid="quiz-submit-button">
                {submitting ? t('quiz.checking') : t('quiz.submit')}
              </button>
            ) : (
              <button onClick={next}
                className="w-full h-14 rounded-full bg-navy text-white font-heading font-bold text-lg shadow-squishy hover:scale-[1.02] active:scale-95 transition-transform duration-200"
                data-testid="quiz-next-button">{t('quiz.next')}</button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
